import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";

import { ProductRecipeHistorySection } from "./product-recipe-history-section";
import { ProductRecipeIngredientTable } from "./product-recipe-ingredient-table";
import { ProductRecipeSummaryCard } from "./product-recipe-summary-card";
import { type useProductRecipeForm } from "./use-product-recipe-form";

/** Recipe editor embedded in the product form: yield, ingredients, live HPP, note, versions. */
export function ProductRecipeSection({
  recipe,
}: {
  recipe: ReturnType<typeof useProductRecipeForm>;
}) {
  return (
    <div className="min-w-0 space-y-3 rounded-lg border bg-muted/20 p-3 text-xs">
      <div>
        <h4 className="text-sm font-semibold">
          Resep & HPP <span className="text-destructive">*</span>
        </h4>
        <p className="text-muted-foreground">
          Produk racikan wajib punya resep. HPP dihitung otomatis dari bahan
          baku.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Label htmlFor="yield-qty" className="text-xs font-medium">
          Hasil per resep
        </Label>
        <NumberInput
          id="yield-qty"
          decimals={2}
          value={recipe.yieldQtyStr ? Number(recipe.yieldQtyStr) : undefined}
          onChange={(n) => recipe.setYieldQtyStr(n ? String(n) : "")}
          className="h-8 w-24 bg-background text-xs"
          placeholder="1"
        />
        <span className="text-muted-foreground">porsi / cup</span>
      </div>

      <ProductRecipeIngredientTable
        rows={recipe.calculatedItems}
        rawMaterials={recipe.rawMaterials}
        uoms={recipe.uoms}
        conversions={recipe.conversions}
        onAdd={recipe.addItem}
        onRemove={recipe.removeItem}
        onChange={recipe.changeItem}
      />

      <ProductRecipeSummaryCard
        totalRecipeCost={recipe.totalRecipeCost}
        yieldQty={recipe.yieldQty}
        computedCogs={recipe.computedCogs}
        marginPercent={recipe.marginPercent}
        storedCogs={recipe.storedCogs}
      />

      <div className="space-y-1">
        <Label htmlFor="recipe-note" className="text-xs font-medium">
          Catatan Resep (Opsional)
        </Label>
        <Textarea
          id="recipe-note"
          placeholder="Contoh: rasio ekstraksi 1:2.5, seduh 28-30 detik..."
          value={recipe.recipeNote}
          onChange={(e) => recipe.setRecipeNote(e.target.value)}
          rows={2}
          className="resize-none bg-background text-xs"
        />
      </div>

      <ProductRecipeHistorySection
        recipes={recipe.recipeHistory}
        isActivating={recipe.activateMutation.isPending}
        onActivate={(id) => recipe.activateMutation.mutate(id)}
      />
    </div>
  );
}
